import {useUser} from '@dofleini/security';
import {proposalrequest, users} from '@mep/pae-commons';
import React, {createContext, useContext, useEffect} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';

import {useIsAdminByRoles} from '@/modules/admin/hooks/useUser';
import {ROUTE_PATH_PROCEDURE} from '@/modules/company/contants/procedure';
import {ROUTE_PATH_EXPEDIENT} from '@/modules/proposalFiles/contants/expedient';
import {
  ROUTE_PATH_MY_PROPOSAL_REQUEST,
  ROUTE_PATH_NEW_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/myProposalRequest';
import {
  ROUTE_PATH_PROPOSAL_REQUEST,
  ROUTE_USER_PATH_COMPANY,
  ROUTE_USER_PATH_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/proposalRequest';
import {useGetOneProposalReducedRequest} from '@/modules/proposalRequest/hooks/useProposalRequest';
import {authentication} from '@/settings';

const {DRAFT, REJECTED} = proposalrequest;
const {USER_TYPES} = users;

const ProposalRequestContext = createContext();

function ProposalByUserProvider({...props}) {
  const {user, isAuthenticated, hasPermission} = useUser();
  const params = useParams();
  const isAdmin = useIsAdminByRoles();
  const {
    data,
    isLoading,
    error
  } = useGetOneProposalReducedRequest({id: params?.id || user?.lastProposal?._id || user?.lastProposal}, {
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && !!(params?.id || user?.lastProposal?._id || user?.lastProposal)
  });
  const {push} = useHistory();
  const {pathname} = useLocation();

  const record = data?.data || null;

  useEffect(() => {
    if (isLoading || hasPermission('ADMIN')) return;
    if (isAuthenticated && (!authentication?.activeOnboarding || user?.onBoardingCompleted)) {
      switch (user?.userType) {
        case USER_TYPES.COMMERCIAL_REGISTER:
        case USER_TYPES.NOTARY_DPJ:
        case USER_TYPES.NOTARY: {
          if (!pathname.startsWith(ROUTE_PATH_EXPEDIENT) && !pathname.startsWith(ROUTE_PATH_PROCEDURE)) {
            push(ROUTE_PATH_EXPEDIENT);
          }
          break;
        }
        case USER_TYPES.HISTORIAN: {
          if (!pathname.startsWith(ROUTE_PATH_PROPOSAL_REQUEST))
            push(ROUTE_PATH_PROPOSAL_REQUEST);
          break;
        }
        case USER_TYPES.USER: {
          // my proposal list
          if (pathname === ROUTE_PATH_MY_PROPOSAL_REQUEST || pathname === ROUTE_PATH_NEW_PROPOSAL_REQUEST)
            return;
          // create or continue editing the proposal
          if (!record || (record?.status === DRAFT && !record.isPartner)) {
            const route = ROUTE_USER_PATH_PROPOSAL_REQUEST;
            if (!pathname.startsWith(route))
              push(route);
          } else {
            // follow the review status of the proposal
            const route = `${ROUTE_USER_PATH_PROPOSAL_REQUEST}/${record?.id}`;
            if (!pathname.startsWith(route) && !pathname.startsWith(ROUTE_USER_PATH_COMPANY))
              push(route);
          }
          break;
        }
        default:
          return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, record, push, isLoading]);

  return (
    <ProposalRequestContext.Provider value={{
      data: record,
      isLoading,
      error,
      haveProposal: !!record,
      id: record?.id,
      wasRejected: record?.status === REJECTED,
      isDraft: record?.status === DRAFT
    }} {...props} />
  );
}

function useProposalByUserProvider() {
  const context = useContext(ProposalRequestContext);
  if (context === undefined) {
    throw new Error('useProposalRequest must be used within a ProposalByUserProvider');
  }

  const {
    data, isLoading, error, id, haveProposal, isDraft, wasRejected
  } = context;

  return {
    id,
    haveProposal,
    detailsRoute: `/requests/${id}`,
    data,
    isLoading,
    error,
    wasRejected,
    isDraft
  };
}

export {ProposalByUserProvider, useProposalByUserProvider};
